import { ReactNode } from 'react';

export type ProgressBarTone = 'accent' | 'success' | 'warning' | 'danger' | 'info';
export type ProgressBarSize = 'sm' | 'md' | 'lg';

export interface ProgressBarProps {
  /** Wartość 0-100 (procent wypełnienia). */
  value: number;
  /** Kolor wypełnienia. Default: accent-primary gradient. */
  tone?: ProgressBarTone;
  size?: ProgressBarSize;
  /** Zaokrąglone rogi. Default true. */
  rounded?: boolean;
  /** Custom label renderowany pod paskiem lub obok (flex layout). */
  label?: ReactNode;
  /** Pokaż procent po prawej (auto-label). */
  showPercent?: boolean;
  className?: string;
  'aria-label'?: string;
}

const HEIGHT_CLASS: Record<ProgressBarSize, string> = {
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
};

const FILL_CLASS: Record<ProgressBarTone, string> = {
  accent: 'bg-gradient-to-r from-accent-primary to-accent-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-accent-info',
};

/**
 * Horyzontalny pasek postępu. Track = surface-muted (widoczny w obu motywach),
 * fill = gradient accent default albo solid kolor statusu. Wzór używany w
 * FinanceDashboardPage dla per-sklep progress vs cel.
 */
export function ProgressBar({
  value,
  tone = 'accent',
  size = 'md',
  rounded = true,
  label,
  showPercent = false,
  className,
  ...rest
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const roundedClass = rounded ? 'rounded-full' : '';

  return (
    <div className={['w-full', className].filter(Boolean).join(' ')}>
      {(label || showPercent) && (
        <div className="flex items-baseline justify-between mb-1.5">
          {label && <div className="text-xs text-text-muted">{label}</div>}
          {showPercent && (
            <span className="text-xs font-semibold text-accent-primary tabular-nums">
              {Math.round(clamped)}%
            </span>
          )}
        </div>
      )}
      <div
        className={`w-full ${HEIGHT_CLASS[size]} bg-surface-muted ${roundedClass} overflow-hidden`}
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={rest['aria-label']}
      >
        <div
          className={`h-full ${FILL_CLASS[tone]} ${roundedClass} transition-all duration-normal ease-smooth`}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}
